/*
  Wishlist Only - Lumin Theme
  Dependencies: formatMoney, resizeImage, calcTimeAgo, trapFocus (theme provided)
*/

// ---------- Helper Functions ----------
window.Shopify = window.Shopify || {};

window.Shopify.formatMoney = function (cents, moneyFormat = window.lumintheme?.moneyFormat || "${{amount}}") {
  if (typeof cents === "string") cents = cents.replace(".", "");
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  function defaultOption(opt, def) { return typeof opt === "undefined" ? def : opt; }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");
    if (isNaN(number) || number == null) return 0;
    number = (number / 100.0).toFixed(precision);
    const parts = number.split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    const cents = parts[1] ? decimal + parts[1] : "";
    return dollars + cents;
  }
  switch (moneyFormat.match(placeholderRegex)[1]) {
    case "amount": value = formatWithDelimiters(cents, 2); break;
    case "amount_no_decimals": value = formatWithDelimiters(cents, 0); break;
    case "amount_with_comma_separator": value = formatWithDelimiters(cents, 2, ".", ","); break;
    case "amount_no_decimals_with_comma_separator": value = formatWithDelimiters(cents, 0, ".", ","); break;
  }
  return moneyFormat.replace(placeholderRegex, value);
};

window.Shopify.resizeImage = function (src, size, crop = "") {
  return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, ".")
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => `_${size}${crop ? `_crop_${crop}` : ""}${match}`);
};

window.Shopify.calcTimeAgo = function (timestamp) {
  const now = Date.now();
  const diff = now - timestamp;
  const times = window.lumintheme?.times || {
    moments: 'moments ago', minute: 'minute ago', minutes: 'minutes ago',
    hour: 'hour ago', hours: 'hours ago', day: 'day ago', days: 'days ago', ago: 'ago'
  };
  let text;
  if (diff < 60000) text = times.moments;
  else if (diff < 3.6e6) { const min = Math.round(diff / 60000); text = min === 1 ? `${min} ${times.minute}` : `${min} ${times.minutes}`; }
  else if (diff < 8.64e7) { const hours = Math.round(diff / 3.6e6); text = hours === 1 ? `${hours} ${times.hour}` : `${hours} ${times.hours}`; }
  else { const days = Math.round(diff / 8.64e7); text = days === 1 ? `${days} ${times.day}` : `${days} ${times.days}`; }
  return `${text} ${times.ago}`;
};

// ---------- Wishlist Drawer Component ----------
class LuminWishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector(".drawer__overlay")?.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => { if (event.target === this) this.close(); });
    this.setIconsAccessibility();
  }
  setIconsAccessibility() {
    document.querySelectorAll('a[href="#lumin-wishlist-drawer"]').forEach((link) => {
      link.addEventListener("click", (event) => { event.preventDefault(); this.open(link); });
      link.addEventListener("keydown", (event) => { if (event.code.toUpperCase() === "SPACE") { event.preventDefault(); this.open(link); } });
    });
  }
  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    setTimeout(() => { this.classList.add("animate", "active"); document.body.classList.add("overflow-hidden"); });
    this.addEventListener("transitionend", () => {
      const containerToTrapFocusOn = this.classList.contains("is-empty") ? this.querySelector(".drawer__inner-empty") : this.querySelector(".lumin-wishlist-drawer");
      const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
      if (window.trapFocus) window.trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
  }
  close() {
    this.classList.remove("active");
    if (window.removeTrapFocus) window.removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }
}
customElements.define("lumin-wishlist-drawer", LuminWishlistDrawer);

// ---------- Wishlist Container (localStorage + UI) ----------
class LuminWishlistContainer extends HTMLElement {
  constructor() {
    super();
    this.setCountBadges();
    this.setContent();
    window.addEventListener("storage", (event) => {
      if (event.key === "lumin-wishlist") { this.setCountBadges(); this.setContent(); }
    });
  }
  get wishlist() {
    try {
      const stored = localStorage.getItem("lumin-wishlist");
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  }
  set wishlist(array) {
    if (!Array.isArray(array)) return;
    localStorage.setItem("lumin-wishlist", JSON.stringify(array));
    window.dispatchEvent(new StorageEvent("storage", { key: "lumin-wishlist", newValue: JSON.stringify(array) }));
  }
  async setProduct(url) {
    let wishlist = this.wishlist;
    const isWishlisted = wishlist.some(e => e.url === url);
    if (isWishlisted) {
      wishlist = wishlist.filter(e => e.url !== url);
    } else {
      try {
        const response = await fetch(`${url}.js`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const product = await response.json();
        wishlist.push({
          url, id: product.id, handle: product.handle, title: product.title,
          img_src: product.featured_image, img_alt: product.featured_image?.alt || "",
          compare_at_price: product.compare_at_price, price: product.price,
          price_varies: product.price_varies, added_at: Date.now(),
        });
      } catch (error) { console.error("Wishlist fetch error:", error); return; }
    }
    this.wishlist = wishlist;
    this.setCountBadges();
    this.setContent();
  }
  setCountBadges() {
    const count = this.wishlist.length;
    document.querySelectorAll(".wishlist-count-bubble").forEach(elem => {
      const span = elem.querySelector("span");
      if (span) span.textContent = count;
      if (count > 0) elem.removeAttribute("hidden");
      else elem.setAttribute("hidden", "hidden");
    });
  }
  setContent() {
    const wishlist = this.wishlist;
    const emptyState = this.querySelector(".drawer__inner-empty.lumin-wishlist-empty");
    const productList = this.querySelector(".lumin-wishlist-drawer-product-list");
    const listWrapper = this.querySelector(".lumin-wishlist-drawer-product-list-wrapper");
    if (!emptyState || !productList || !listWrapper) return;

    if (wishlist.length > 0) {
      emptyState.classList.add("hidden");
      this.classList.remove("is-empty");
      let imgWidth = 600, imgHeight = 600;
      if (this.dataset.imgRatio === "ratio-4x3") { imgWidth = 600; imgHeight = Math.round((600 / 4) * 3); }
      else if (this.dataset.imgRatio === "ratio-3x4") { imgWidth = Math.round((600 / 4) * 3); imgHeight = 600; }
      let html = "";
      wishlist.forEach(product => {
        html += `
          <div class="lumin-wishlist-drawer-product-list-item lumin-grid-product-list-item" role="listitem">
            <a href="${product.url}" tabindex="-1">
              <img src="${window.Shopify.resizeImage(product.img_src, `${imgWidth}x${imgHeight}`, "center")}"
                class="product-card-img img-fluid ${this.dataset.imgBorder || ""}" alt="${product.img_alt}"
                width="${imgWidth}" height="${imgHeight}" loading="lazy">
            </a>
            <div>
              <h4 class="title h5 text-truncate"><a href="${product.url}" class="full-unstyled-link">${product.title}</a></h4>
              <div class="price"><div class="price__container"><span class="price-item price-item--last">${window.Shopify.formatMoney(product.price)}</span></div></div>
              <div class="lumin-grid-product-list-item-added-at">
                <svg xmlns="http://www.w3.org/2000/svg" class="me-2" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                ${window.Shopify.calcTimeAgo(product.added_at)}
              </div>
            </div>
            <button class="lumin-wishlist-product-item-btn-remove" type="button" aria-label="Remove" data-product-url="${product.url}">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16" width="16" height="16"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"></path><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"></path></svg>
            </button>
          </div>`;
      });
      productList.innerHTML = html;
      listWrapper.removeAttribute("hidden");
      document.querySelectorAll(".lumin-wishlist-product-item-btn-remove").forEach(btn => {
        btn.addEventListener("click", () => {
          this.setProduct(btn.dataset.productUrl);
          document.querySelectorAll(".lumin-wishlist-btn").forEach(btn => btn.adjustBtn());
        });
      });
    } else {
      emptyState.classList.remove("hidden");
      this.classList.add("is-empty");
      productList.innerHTML = "";
      listWrapper.setAttribute("hidden", "hidden");
    }
  }
}
customElements.define("lumin-wishlist-container", LuminWishlistContainer);

// ---------- Wishlist Button Component (Add/Remove Toggle) ----------
class LuminWishlistBtn extends HTMLElement {
  constructor() {
    super();
    this.btn = this.querySelector("button");
    if (!this.btn) return;
    this.wishlistContainer = document.querySelector("lumin-wishlist-container");
    if (!this.wishlistContainer) return;
    this.adjustBtn();
    this.btn.addEventListener("click", async (event) => {
      event.preventDefault();
      event.stopPropagation();
      await this.wishlistContainer.setProduct(this.dataset.productUrl);
      this.adjustBtn();
    });
  }
  adjustBtn() {
    if (!this.wishlistContainer) return;
    const isWishlisted = this.wishlistContainer.wishlist.some(e => e.url === this.dataset.productUrl);
    const wishlistText = window.kondasoft?.wishlist || { add: "Add to wishlist", remove: "Remove from wishlist" };
    if (isWishlisted) {
      this.btn.classList.add("active");
      this.btn.setAttribute("aria-label", wishlistText.remove);
      this.btn.setAttribute("aria-pressed", "true");
    } else {
      this.btn.classList.remove("active");
      this.btn.setAttribute("aria-label", wishlistText.add);
      this.btn.setAttribute("aria-pressed", "false");
    }
  }
}
customElements.define("lumin-wishlist-btn", LuminWishlistBtn);

